import { useState } from "react";
import { useNavigate } from "react-router";
import type { PracticeMode } from "../../domain/practice/engine.ts";
import { eligibleMeanings, nextReviewTime, unavailableOverdueMeanings } from "../../domain/practice/engine.ts";
import { eligibleCardMeanings, hiddenWordLeakCount, nextCardReviewTime } from "../../domain/practice/cards.ts";
import type { CardDirection } from "../../domain/vocabulary/types.ts";
import { formatLocalDateTime } from "../../application/vocabulary/queries.ts";
import { useAppServices } from "../hooks/AppContext.tsx";
import { useNow, usePractice, useVocabularySnapshot } from "../hooks/bindings.ts";
import { Frame, PrimaryButton, Screen, TopBar } from "../components/Shell.tsx";
import styles from "../design-system/ui.module.css";

const DIRECTIONS: Array<{ id: CardDirection; title: string; detail: string }> = [
  { id: "hide_meaning", title: "Meaning", detail: "See the word or phrase, then explain the meaning." },
  { id: "hide_word", title: "Word or phrase", detail: "See the definition, then recall the English expression." },
];

const MODES: Array<{ id: PracticeMode; title: string; detail: string; cardDetail: string }> = [
  {
    id: "due",
    title: "Due reviews",
    detail: "Meanings whose review time has arrived.",
    cardDetail: "Cards due in the direction you chose.",
  },
  {
    id: "extra",
    title: "Extra practice",
    detail: "Optional practice that does not skip the schedule.",
    cardDetail: "Any saved card in this direction, including ones that are not due yet.",
  },
  {
    id: "confusable",
    title: "Confusable words",
    detail: "Supporting practice for similar-word comparisons.",
    cardDetail: "Flashcards for meanings that still have a saved similar-word relation.",
  },
  {
    id: "select",
    title: "Select words",
    detail: "Choose specific meanings to practice.",
    cardDetail: "Choose specific meanings. Each one stays its own card.",
  },
];

export function PracticeOptionsScreen() {
  const { practice } = useAppServices();
  const state = usePractice();
  const snapshot = useVocabularySnapshot();
  const navigate = useNavigate();
  const now = useNow();
  const [size, setSize] = useState(String(state.setup.size));
  const cards = state.setup.format === "cards";
  const eligible = cards
    ? eligibleCardMeanings(snapshot, state.setup.mode, state.setup.direction, state.setup.selectedMeaningIds, now).length
    : eligibleMeanings(snapshot, state.setup.mode, state.setup.selectedMeaningIds, now).length;
  const stale = cards ? 0 : unavailableOverdueMeanings(snapshot, now).length;
  const leaks = cards && state.setup.direction === "hide_word" ? hiddenWordLeakCount(snapshot) : 0;
  const nextReview = cards
    ? nextCardReviewTime(snapshot, state.setup.direction, now)
    : nextReviewTime(snapshot, now);
  const actualSize = Math.min(Number(size) || state.setup.size, Math.max(eligible, 1));
  const noDue = state.setup.mode === "due" && eligible === 0;

  function availableFor(mode: PracticeMode) {
    return cards
      ? eligibleCardMeanings(snapshot, mode, state.setup.direction, state.setup.selectedMeaningIds, now).length
      : eligibleMeanings(snapshot, mode, state.setup.selectedMeaningIds, now).length;
  }

  function start(mode: PracticeMode = state.setup.mode) {
    if (mode !== state.setup.mode) {
      practice.setMode(mode);
    }
    const available = availableFor(mode);
    const requested = Number(size);
    const nextSize = Number.isInteger(requested) && requested > 0 ? Math.min(requested, Math.max(available, 1)) : 1;
    practice.setSize(nextSize);
    setSize(String(nextSize));
    if (mode === "select") {
      navigate("/practice/select");
      return;
    }
    if (practice.start()) {
      navigate("/practice/session");
    }
  }

  return (
    <Frame>
      <Screen withNav={false}>
        <TopBar title={cards ? "Cards" : "Write answer"} backTo="/practice" />
        <header className={styles.pageHead}>
          <h1>{cards ? "Card direction" : "Which meanings?"}</h1>
          <p>
            {cards
              ? "Choose what stays hidden, then which cards to include."
              : "Choose which meanings belong in this written session."}
          </p>
        </header>
        {cards ? (
          <fieldset className={`${styles.stack} ${styles.choiceGroup}`}>
            <legend>Hide</legend>
            {DIRECTIONS.map((direction) => {
              const active = state.setup.direction === direction.id;
              return (
                <label key={direction.id} className={`${styles.radioRow} ${active ? styles.radioRowActive : ""}`}>
                  <input
                    type="radio"
                    name="direction"
                    checked={active}
                    onChange={() => practice.setDirection(direction.id)}
                  />
                  <span>
                    <strong>{direction.title}</strong>
                    <div className={styles.sub}>{direction.detail}</div>
                  </span>
                </label>
              );
            })}
          </fieldset>
        ) : null}
        <fieldset className={`${styles.stack} ${styles.choiceGroup}`}>
          <legend>{cards ? "Which cards?" : "Include"}</legend>
          {MODES.map((mode) => {
            const active = state.setup.mode === mode.id;
            return (
              <label key={mode.id} className={`${styles.radioRow} ${active ? styles.radioRowActive : ""}`}>
                <input type="radio" name="mode" checked={active} onChange={() => practice.setMode(mode.id)} />
                <span>
                  <strong>{mode.title}</strong>
                  <div className={styles.sub}>{cards ? mode.cardDetail : mode.detail}</div>
                </span>
              </label>
            );
          })}
        </fieldset>
        {cards ? (
          <p className={styles.sub}>
            A correct or incorrect mark updates only this direction. Written review and the other card direction keep
            their own dates.
          </p>
        ) : null}
        {noDue ? (
          <div className={styles.banner}>
            <div>
              <strong>No reviews due.</strong>
              {nextReview ? <div className={styles.sub}>Next review {formatLocalDateTime(nextReview)}.</div> : null}
              {stale ? (
                <div className={styles.sub}>
                  {stale} overdue {stale === 1 ? "meaning needs" : "meanings need"} new exercises before review.
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <p className={styles.countLabel}>
            {eligible} {eligible === 1 ? "card" : "cards"} available
            {state.setup.mode !== "select" ? ` · session will use ${Math.min(actualSize, eligible)}` : ""}
            {stale ? ` · ${stale} overdue ${stale === 1 ? "meaning needs" : "meanings need"} new exercises` : ""}
          </p>
        )}
        {leaks ? (
          <p className={styles.sub}>
            {leaks} {leaks === 1 ? "definition contains" : "definitions contain"} the saved word or phrase, so{" "}
            {leaks === 1 ? "that meaning is" : "those meanings are"} left out of Hide word or phrase. This check looks
            for the saved spelling, not every inflection. Choose Hide meaning to practise them.
          </p>
        ) : null}
        <label className={styles.field}>
          Session size
          <input
            className={styles.input}
            type="number"
            min={1}
            value={size}
            onChange={(event) => setSize(event.target.value)}
          />
        </label>
        {state.warning && !noDue ? <p className={styles.error}>{state.warning}</p> : null}
        {noDue ? (
          <div className={styles.stack}>
            <PrimaryButton onClick={() => start("extra")} disabled={availableFor("extra") === 0}>
              Extra practice
            </PrimaryButton>
            <PrimaryButton secondary onClick={() => start("select")}>
              Select words
            </PrimaryButton>
          </div>
        ) : (
          <PrimaryButton onClick={() => start()} disabled={state.setup.mode !== "select" && eligible === 0}>
            {state.setup.mode === "select" ? "Choose meanings" : "Start Practice"}
          </PrimaryButton>
        )}
      </Screen>
    </Frame>
  );
}
